import * as Sentry from '@sentry/nuxt'
import { API_BASE } from '~/lib/platform/apiBase'
import { useGroupsStore } from '~/stores/groups'
import { useFriendsStore } from '~/stores/friends'
import { useSubscriptionsStore } from '~/stores/subscriptions'
import { useAgentApprovalsStore } from '~/stores/agentApprovals'
import { useNotificationSettingsStore } from '~/stores/notificationSettings'
import { createSignalRConnection, type SignalRInvocation } from '~/lib/signalr/connection'
import { applyUserProfileUpdated, dualCaseNumber, dualCaseString, parseEphemeralMessage } from '~/lib/signalr/payload'
import { useMessagesStore } from '~/stores/messages'
import { applyRemotePreferredStatus } from '~/composables/useUserStatus'
import { FriendshipStatus, SIGNALR_FRIENDSHIP_STATUS, type FriendSignalREventPayload } from '~/types/friends'

export interface UserEvent {
  userId: string
  type: string
  payloadJson: string
  timestamp: number
}

const FRIEND_EVENT_TYPES = new Set([
  'FriendRequestCreated',
  'FriendRequestSent',
  'FriendRequestAccepted',
  'FriendRequestDeclined',
  'FriendRequestCancelled',
  'FriendRemoved',
  'FriendUserBlocked',
])

// Module-scoped so every `useUsersSignalR()` call site (app.vue, index.vue, ...) shares one real
// connection instead of each opening its own socket, and `connect()` is safely idempotent.
const connection = createSignalRConnection({
  label: '[UsersSignalR]',
  hubUrl: `${API_BASE}/hubs/users`,
  connectErrorAction: 'users_signalr_connect',
  onInvocation: (invocation: SignalRInvocation) => {
    if (invocation.target === 'user-events') {
      handleUserEvent(invocation.arguments?.[0] as UserEvent)
    }
  },
})

// The server never buffers events for a disconnected client, so any event missed while the
// socket was down must be recovered by a full list refetch rather than trusting the eventual
// reconnect to "catch up" — see the friends real-time sync rules. `hasConnectedOnce` distinguishes
// the very first handshake (nothing to resync yet) from a later reconnect.
let hasConnectedOnce = false
watch(connection.isConnected, (isConnected) => {
  if (!isConnected) return
  // Pending agent approvals (badge on «Боты и агенты») are loaded on every connect — the first one
  // after login/app start included — so the hub's events start from a complete list.
  void useAgentApprovalsStore().fetch()
  if (hasConnectedOnce) {
    console.log('[UsersSignalR] Reconnected — refetching friends state')
    const friendsStore = useFriendsStore()
    void friendsStore.loadAll()
    void friendsStore.fetchBlockedUsers()
    // Notification settings changed on another device while we were away.
    useNotificationSettingsStore().refreshLoaded()
  }
  hasConnectedOnce = true
})

function handleUserEvent(event: UserEvent) {
  const groupsStore = useGroupsStore()

  console.log('[UsersSignalR] Received event:', event)

  if (event.type === 'UserStatusChanged') {
    try {
      const payload = JSON.parse(event.payloadJson) as Record<string, unknown>
      const userId = dualCaseString(payload, 'userId', 'UserId') || event.userId
      const presenceStatus = dualCaseNumber(payload, 'presenceStatus', 'PresenceStatus', NaN)
      const status = dualCaseString(payload, 'status', 'Status')

      if (userId && !Number.isNaN(presenceStatus)) {
        groupsStore.setUserPresenceStatus(userId, presenceStatus, status)
      }
      // Only this hub's (personal) copy carries what the account picked: another device may have changed it.
      const preferredStatus = dualCaseNumber(payload, 'preferredStatus', 'PreferredStatus', NaN)
      if (!Number.isNaN(preferredStatus)) applyRemotePreferredStatus(preferredStatus)
    } catch (error) {
      console.error('[UsersSignalR] Failed to parse UserStatusChanged payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_user_status_changed_payload' } })
    }
  } else if (event.type === 'UserProfileUpdated') {
    try {
      applyUserProfileUpdated(event)
    } catch (error) {
      console.error('[UsersSignalR] Failed to parse UserProfileUpdated payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_user_profile_updated_payload' } })
    }
  } else if (event.type === 'EphemeralMessageCreated') {
    // Quick replies from an AI agent to whoever called it — only this user gets the event.
    try {
      const message = parseEphemeralMessage(JSON.parse(event.payloadJson) as Record<string, unknown>)
      if (message) useMessagesStore().addEphemeral(message)
    } catch (error) {
      console.error('[UsersSignalR] Failed to parse EphemeralMessageCreated payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_ephemeral_message_payload' } })
    }
  } else if (event.type === 'UserSubscriptionUpdated') {
    // Payment confirmed, renewed or expired server-side (see proto/subscriptions.proto). The payload
    // shape isn't relied on - the authoritative state is simply refetched.
    void useSubscriptionsStore().handleRealtimeUpdate()
  } else if (event.type === 'AgentToolApprovalRequested' || event.type === 'AgentToolApprovalResolved') {
    // Requested: the whole `AgentToolApproval` (camelCase, enums as numbers). Resolved: {approvalId, botId, status}.
    try {
      const payload = JSON.parse(event.payloadJson) as Record<string, unknown>
      useAgentApprovalsStore().applyRealtime(event.type, payload)
    } catch (error) {
      console.error(`[UsersSignalR] Failed to parse ${event.type} payload:`, error)
      Sentry.captureException(error, { extra: { action: 'parse_agent_approval_payload', type: event.type } })
    }
  } else if (event.type === 'NotificationSettingsChanged') {
    // The whole `GroupNotificationSettings` of one group, changed on this or another device. Key
    // casing and enum encoding are normalized by the store (snake_case, camelCase or PascalCase).
    try {
      useNotificationSettingsStore().applyRemote(JSON.parse(event.payloadJson))
    } catch (error) {
      console.error('[UsersSignalR] Failed to parse NotificationSettingsChanged payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_notification_settings_payload' } })
    }
  } else if (FRIEND_EVENT_TYPES.has(event.type)) {
    try {
      // Despite being a purpose-built DTO, this payload arrives PascalCase (e.g. `OtherUsername`,
      // `Status`) just like the raw C# entity serializations elsewhere — same dualCaseX()
      // treatment needed. `Status` itself arrives as the raw proto ordinal (a number), not the
      // enum's string name.
      const raw = JSON.parse(event.payloadJson) as Record<string, unknown>
      const rawStatus = raw.status ?? raw.Status
      const status =
        typeof rawStatus === 'number'
          ? (rawStatus as FriendshipStatus)
          : (SIGNALR_FRIENDSHIP_STATUS[String(rawStatus)] ?? FriendshipStatus.Unknown)

      const payload: FriendSignalREventPayload = {
        friendId: dualCaseString(raw, 'friendId', 'FriendId'),
        otherUserId: dualCaseString(raw, 'otherUserId', 'OtherUserId'),
        otherUsername: dualCaseString(raw, 'otherUsername', 'OtherUsername'),
        otherDisplayName: dualCaseString(raw, 'otherDisplayName', 'OtherDisplayName') || null,
        otherAvatarUrl: dualCaseString(raw, 'otherAvatarUrl', 'OtherAvatarUrl'),
        status,
        message: dualCaseString(raw, 'message', 'Message') || null,
        timestamp: dualCaseString(raw, 'timestamp', 'Timestamp'),
      }
      useFriendsStore().applyRealtimeEvent(event.type, payload)
    } catch (error) {
      console.error(`[UsersSignalR] Failed to parse ${event.type} payload:`, error)
      Sentry.captureException(error, { extra: { action: 'parse_friend_event_payload', type: event.type } })
    }
  }
}

export function useUsersSignalR() {
  return {
    isConnected: connection.isConnected,
    connect: connection.connect,
    disconnect: connection.disconnect,
  }
}
